import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useGlobalContext } from '../../../../../utils/Context'
import { getInvoiceDetails } from '../../../../../utils/WeightDiscrepancyApi'
import FormLoader from '../../../../../components/loader/FormLoader'
import PaymentModal from './PaymentModal'

interface InvoiceDetailsPageProps {
  invoiceId: string
  onBack: () => void
  onPaymentSuccess: () => void
}

function InvoiceDetailsPage({
  invoiceId,
  onBack,
  onPaymentSuccess,
}: InvoiceDetailsPageProps) {
  const { notify } = useGlobalContext()
  const [invoice, setInvoice] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  useEffect(() => {
    setLoading(true)
    getInvoiceDetails(invoiceId)
      .then((result: any) => {
        if (result.status === 200) {
          setInvoice(result.data)
        } else {
          notify('error', result.message)
          onBack()
        }
        setLoading(false)
      })
      .catch(() => onBack())
  }, [invoiceId])

  const fmt = (n: number) => `₦${n.toLocaleString()}`

  const statusInfo = (status: string) => {
    switch (status) {
      case 'sent':
        return { cls: 'pending', label: 'Pending' }
      case 'overdue':
        return { cls: 'overdue', label: 'Overdue' }
      case 'paid':
        return { cls: 'paid', label: 'Paid' }
      default:
        return { cls: '', label: status }
    }
  }

  if (loading) {
    return (
      <div
        className='home'
        style={{
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FormLoader position='center' />
      </div>
    )
  }

  if (!invoice) return null

  const si = statusInfo(invoice.status)
  const remaining =
    invoice.remainingAmount ?? invoice.totalAmount - invoice.amountPaid

  return (
    <>
      <div className='home'>
        {/* Back */}
        <button className='wd-back-btn' onClick={onBack}>
          <Icon icon='mdi:arrow-left' width='18' />
          Back to Invoices
        </button>

        {/* Page header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '1rem',
            marginBottom: '1.75rem',
          }}
        >
          <div className='wd-page-header' style={{ marginBottom: 0 }}>
            <div className='wd-header-icon'>
              <Icon
                icon='mdi:file-document-outline'
                width='26'
                color='#0056b3'
              />
            </div>
            <div>
              <h1>Invoice Details</h1>
            </div>
          </div>

          <div className='wd-page-actions'>
            {invoice.invoiceUrl && (
              <a
                href={invoice.invoiceUrl}
                target='_blank'
                rel='noreferrer'
                className='wd-btn-outline'
              >
                <Icon icon='mdi:download' width='18' />
                Download PDF
              </a>
            )}
            {invoice.status !== 'paid' && (
              <button
                className='wd-btn-primary'
                onClick={() => setShowPaymentModal(true)}
              >
                <Icon icon='mdi:credit-card-outline' width='18' />
                Pay Now
              </button>
            )}
          </div>
        </div>

        {/* Stats cards */}
        <div className='wd-stats-grid'>
          <div className='wd-stat-card wd-stat-blue'>
            <span className='wd-stat-label'>Total Amount</span>
            <p className='wd-stat-value'>{fmt(invoice.totalAmount)}</p>
          </div>
          <div className='wd-stat-card wd-stat-green'>
            <span className='wd-stat-label'>Amount Paid</span>
            <p className='wd-stat-value'>{fmt(invoice.amountPaid ?? 0)}</p>
          </div>
          <div
            className={`wd-stat-card ${remaining > 0 ? 'wd-stat-red' : 'wd-stat-green'}`}
          >
            <span className='wd-stat-label'>Remaining</span>
            <p className='wd-stat-value'>{fmt(remaining)}</p>
          </div>
          <div className='wd-stat-card wd-stat-gray'>
            <span className='wd-stat-label'>Status</span>
            <span
              className={`wd-badge ${si.cls}`}
              style={{ marginTop: '4px', display: 'inline-block' }}
            >
              {si.label}
            </span>
          </div>
        </div>

        {/* Invoice info */}
        <div className='wd-section-card'>
          <div className='wd-section-header'>
            <p>Invoice Information</p>
          </div>
          <div className='wd-section-body'>
            <div className='wd-info-grid'>
              <div>
                <span className='wd-info-label'>Date Issued</span>
                <p className='wd-info-value'>
                  {new Date(invoice.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
              <div>
                <span className='wd-info-label'>Payment Deadline</span>
                <p
                  className={`wd-info-value${invoice.status === 'overdue' ? ' overdue' : ''}`}
                >
                  {new Date(invoice.deadline).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                  {invoice.status === 'overdue' && (
                    <Icon
                      icon='mdi:alert-circle'
                      width='15'
                      color='#dc2626'
                      style={{ marginLeft: '6px', verticalAlign: 'middle' }}
                    />
                  )}
                </p>
              </div>
              <div>
                <span className='wd-info-label'>Total Items</span>
                <p className='wd-info-value'>
                  {invoice.items?.length ?? 0} Booking
                  {(invoice.items?.length ?? 0) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Package items */}
        <div className='wd-section-card'>
          <div className='wd-section-header'>
            <p>Booking Items</p>
          </div>
          <div className='wd-scroll-table'>
            <table>
              <thead>
                <tr
                  style={{
                    background: '#f8fafc',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  {[
                    'Tracking ID',
                    'Description',
                    'Old Weight',
                    'New Weight',
                    'Diff',
                    'Amount',
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '0.85rem 1.25rem',
                        textAlign: 'left',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        textTransform: 'uppercase' as const,
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap' as const,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoice.items?.map((item: any, i: number) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td
                      style={{
                        padding: '1rem 1.25rem',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 700,
                        color: '#111827',
                      }}
                    >
                      {item.trackingId}
                    </td>
                    <td
                      style={{
                        padding: '1rem 1.25rem',
                        color: '#6b7280',
                        fontSize: '0.85rem',
                      }}
                    >
                      {item.description || '—'}
                    </td>
                    <td
                      style={{
                        padding: '1rem 1.25rem',
                        color: '#374151',
                        whiteSpace: 'nowrap' as const,
                      }}
                    >
                      {item.oldWeight}kg
                    </td>
                    <td
                      style={{
                        padding: '1rem 1.25rem',
                        color: '#dc2626',
                        fontWeight: 600,
                        whiteSpace: 'nowrap' as const,
                      }}
                    >
                      {item.newWeight}kg
                    </td>
                    <td
                      style={{
                        padding: '1rem 1.25rem',
                        color: '#d97706',
                        fontWeight: 600,
                        whiteSpace: 'nowrap' as const,
                      }}
                    >
                      +{item.newWeight - item.oldWeight}kg
                    </td>
                    <td
                      style={{
                        padding: '1rem 1.25rem',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 700,
                        color: '#0056b3',
                        textAlign: 'right' as const,
                        whiteSpace: 'nowrap' as const,
                      }}
                    >
                      {fmt(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr
                  style={{
                    background: '#f8fafc',
                    borderTop: '2px solid #e5e7eb',
                  }}
                >
                  <td
                    colSpan={5}
                    style={{
                      padding: '1rem 1.25rem',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#374151',
                      fontSize: '0.875rem',
                    }}
                  >
                    Total
                  </td>
                  <td
                    style={{
                      padding: '1rem 1.25rem',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: '#0056b3',
                      fontSize: '1rem',
                      textAlign: 'right' as const,
                    }}
                  >
                    {fmt(invoice.totalAmount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {showPaymentModal && (
        <PaymentModal
          invoiceId={invoice._id}
          amount={remaining}
          closeModal={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false)
            onPaymentSuccess()
          }}
        />
      )}
    </>
  )
}

export default InvoiceDetailsPage
